'use client';

import { useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import {
  fetchWithTracingAndLogging,
  shouldRetryOnServerError,
} from '@/app/components/hotel-detail/common/fetchWithTracing';
import { getLoggingInfo } from '@/app/components/hotel-detail/lib/loggingInfo';
import { BASE_PATH } from '@/app/components/search/utils/path';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { isValidHotelId } from '@/app/components/hotel-detail/common/common.helper';
import { hasReviewsContent } from '@/app/common/reviews/reviewSource';
import { useFetchHotelPromotions } from './useFetchHotelPromotions';
import { useFetchHotelStatic } from './useFetchHotelStatic';

export const GUEST_REVIEWS_PER_PAGE = 20;

export type FilterType = 'sort' | 'traveller' | 'months';
export type SortFilterValue = 'date_desc' | 'rating_desc' | 'rating_asc';
export type TravellersFilterValue = 'all' | 'partner' | 'self' | 'family' | 'family_with_children' | 'friends' | 'pet';
export type MonthsFilterValue = 'all' | '3,5' | '6,8' | '9,11' | '12,2';

/** Reviews behind each filter option, by option value. An option left without a count could not be asked. */
export type GuestReviewsFilterCounts = {
  traveller: Partial<Record<TravellersFilterValue, number>>;
  months: Partial<Record<MonthsFilterValue, number>>;
};

const COUNTED_TRAVELLER_FILTERS: TravellersFilterValue[] = [
  'partner',
  'self',
  'family',
  'family_with_children',
  'friends',
  'pet',
];
const COUNTED_MONTHS_FILTERS: MonthsFilterValue[] = ['3,5', '6,8', '9,11', '12,2'];

const SWR_OPTIONS = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// Whether the hotel-reviews request may be made at all, and how to make it: the reviews list and the
// filter counts share it, so neither asks for reviews the other would not show.
function useGuestReviewsRequest() {
  const { pageType, internalHotelId } = useInitialParams();
  const { searchInfoData, commonData, localization } = usePageWrapperData();
  const { data: hotelPromotions } = useFetchHotelPromotions();
  const { data: staticData } = useFetchHotelStatic();
  const { offersData } = useOffersContext();

  // Kill switch: when reviews are disabled for the BP, skip the fetch entirely (null key) so no
  // provider reviews render anywhere they consume this hook (section, modal).
  const { reviewsDisabled } = localization.configValuesApp;

  // Expedia (provider 117, sources EXPEDIA_GROUP | HOTELS_COM | VRBO) is the only provider wired
  // into hotel-reviews, and its content may only be shown while we sell Expedia inventory. We used
  // to send excluded_providers=117 on flash sales / hotels without Expedia offers, which can only
  // return an empty list — skip the request instead (MPH-863). hasExpediaOffers stays undefined
  // until rooms load, so this also holds the fetch until we can decide (before, that window fired
  // an extra guaranteed-empty call that was refetched once offers arrived).
  const expediaReviewsSellable = offersData?.hasExpediaOffers === true && !hotelPromotions?.isFlashSale;

  // Provider reviews only come with the hotel's own TripAdvisor/TrustYou reviews: a hotel without that
  // score gets no widget, so no request either (MPH-602). The search placeholder carries no review
  // source, so this also holds the fetch until travel-api has answered.
  const hasScore = hasReviewsContent(staticData?.hotelOpinion);

  // fr_FR never renders guest reviews, so don't fetch what would be dropped (MPH-863).
  const skipFetch =
    reviewsDisabled ||
    !isValidHotelId(internalHotelId) ||
    localization.locale === 'fr-FR' ||
    !expediaReviewsSellable ||
    !hasScore;

  const fetchReviews = (url: string, module: string) =>
    fetchWithTracingAndLogging<HotelReviewsResponse>(
      url,
      {
        method: 'GET',
        headers: {
          'content-type': 'application/json',
        },
      },
      getLoggingInfo(pageType, 'guestReviews', module),
      searchInfoData.searchTraceParent,
      commonData
    );

  return { skipFetch, internalHotelId, locale: localization.locale, fetchReviews };
}

export function useFetchGuestReviews() {
  const { skipFetch, internalHotelId, locale, fetchReviews } = useGuestReviewsRequest();
  const [sort, setSort] = useState<SortFilterValue>('date_desc');
  const [travellerFilter, setTravellerFilter] = useState<TravellersFilterValue>('all');
  const [monthsFilter, setMonthsFilter] = useState<MonthsFilterValue>('all');

  const swrInfinite = useSWRInfinite(
    skipFetch ? () => null : getKey(internalHotelId, locale, sort, travellerFilter, monthsFilter),
    async (url) => fetchReviews(url, 'HotelPageRoomSection'),
    {
      ...SWR_OPTIONS,
      revalidateFirstPage: false,
      shouldRetryOnError: shouldRetryOnServerError,
    }
  );
  return { ...swrInfinite, setSort, setTravellerFilter, setMonthsFilter };
}

/** Whether there are provider guest reviews to show — the check the guest reviews section renders on. */
export function useHasGuestReviews() {
  const { data } = useFetchGuestReviews();
  return !!data?.[0]?.data?.reviews?.length;
}

/**
 * How many reviews each traveller type and each season would list, so the reviews modal can grey out
 * the options that list none (MPH-602). hotel-reviews returns no facets, so every option is asked for
 * its total with a one-review page — only the modal calls this, so the requests wait until it opens.
 * Each option counts against all the reviews, not against the other filter's choice.
 */
export function useGuestReviewsFilterCounts() {
  const { skipFetch, internalHotelId, locale, fetchReviews } = useGuestReviewsRequest();

  return useSWR<GuestReviewsFilterCounts>(
    skipFetch ? null : `guest-reviews-filter-counts:${internalHotelId}:${locale}`,
    async () => {
      const countReviews = async (travellerFilter: TravellersFilterValue, monthsFilter: MonthsFilterValue) => {
        const url = getGuestReviewsUrl(internalHotelId, locale, 0, 1, 'date_desc', travellerFilter, monthsFilter);
        try {
          return (await fetchReviews(url, 'GuestReviewsFilterCounts'))?.data?.totalReviewsCount;
        } catch {
          // Unknown rather than empty: the option stays selectable.
          return undefined;
        }
      };
      const [travellerCounts, monthsCounts] = await Promise.all([
        Promise.all(COUNTED_TRAVELLER_FILTERS.map((value) => countReviews(value, 'all'))),
        Promise.all(COUNTED_MONTHS_FILTERS.map((value) => countReviews('all', value))),
      ]);
      return {
        traveller: Object.fromEntries(COUNTED_TRAVELLER_FILTERS.map((value, index) => [value, travellerCounts[index]])),
        months: Object.fromEntries(COUNTED_MONTHS_FILTERS.map((value, index) => [value, monthsCounts[index]])),
      };
    },
    SWR_OPTIONS
  );
}

function getKey(
  internalHotelId: number,
  locale: string,
  sort: SortFilterValue,
  travellerFilter: TravellersFilterValue,
  monthsFilter: MonthsFilterValue
) {
  return (pageIndex: number, previousPageData: HotelReviewsResponse) => {
    if (previousPageData && pageIndex * GUEST_REVIEWS_PER_PAGE >= previousPageData.data.totalReviewsCount) return null;

    return getGuestReviewsUrl(
      internalHotelId,
      locale,
      pageIndex * GUEST_REVIEWS_PER_PAGE,
      GUEST_REVIEWS_PER_PAGE,
      sort,
      travellerFilter,
      monthsFilter
    );
  };
}

function getGuestReviewsUrl(
  internalHotelId: number,
  locale: string,
  offset: number,
  limit: number,
  sort: SortFilterValue,
  travellerFilter: TravellersFilterValue,
  monthsFilter: MonthsFilterValue
) {
  return `${BASE_PATH}/api/hotel-detail/hotel-reviews?id=${internalHotelId}&locale=${locale}&offset=${offset}&limit=${limit}&sort=${sort}${travellerFilter && travellerFilter !== 'all' ? `&travel_companion=${travellerFilter}` : ''}${monthsFilter && monthsFilter !== 'all' ? `&months=${monthsFilter}` : ''}`;
}
